import { useTranslation } from "react-i18next";
import { WalletProviderConfiguration } from "store/config";
import { useEffect, useState } from "react";
import {
  EWALLET_TYPE,
  extractError,
  getDefaultInputValue,
  renderConditionalProp,
} from "utility";
import classes from "./EWalletSelector.module.scss";
import { MakePaymentClick } from "components/sliderPage/makePayment/MakePayment";
import { useAddNotification, UseInput, useInput } from "hooks";
import { ApplePay } from "../applePay/ApplePay";
import { PaymentActionType } from "../addPaymentMethods/AddPaymentMethods";
import type { PaymentMethod } from "store/paymentMethods/paymentMethods-state";
import { useRootDispatch } from "store";
import { updateSlide } from "store/common/common-slice";
import { useRequestDispatch } from "store/requestHooks";
import {
  savePaymentMethod,
  SavePaymentMethod,
} from "store/paymentMethods/paymentMethods-action";
import { GooglePay } from "../googlePay/GooglePay";

interface EWalletSelectorProps {
  providers: WalletProviderConfiguration[];
  paymentHandler?: Function;
  paymentAmount?: number;
  action: string; // PaymentActionType
  buttonClick?: MakePaymentClick;
  updateButtonClick?: (
    updateFn: (prev: MakePaymentClick) => MakePaymentClick,
  ) => void;
  selectedWalletType?: number | null;
  setSelectedWalletType?: (walletType: number) => void;
}

export const EWalletSelector = ({
  providers,
  paymentHandler,
  paymentAmount,
  action,
  buttonClick,
  updateButtonClick,
  selectedWalletType,
  setSelectedWalletType,
}: EWalletSelectorProps) => {
  const { t } = useTranslation(["payment"]);
  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch();
  const addNotification = useAddNotification();

  const ack: UseInput = useInput(getDefaultInputValue(false));
  const [disabled, setDisabled] = useState(false);
  const [walletPaymentMethod, setWalletPaymentMethod] =
    useState<PaymentMethod | null>(null);

  const nickName: UseInput = useInput(getDefaultInputValue(""));

  useEffect(() => {
    if (buttonClick?.scheduleFlag) {
      ack.setValue(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonClick?.scheduleFlag]);

  useEffect(() => {
    if (!buttonClick?.count) {
      return;
    }
    if (!walletPaymentMethod) {
      resetButtonClick();
      return;
    }

    if (ack.value) {
      handleSaveClick();
    } else {
      if (paymentHandler) paymentHandler(walletPaymentMethod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonClick?.count]);

  const resetButtonClick = () => {
    if (updateButtonClick) {
      updateButtonClick((prev) => ({
        ...prev,
        count: 0,
      }));
    }
  };

  const handleWalletTokenReceived = (payload: any, walletType: number) => {
    setDisabled(false);
    setSelectedWalletType?.(walletType);

    if (payload) {
      const jsonPayload =
        walletType === EWALLET_TYPE.ApplePay
          ? JSON.stringify({ token: payload.token })
          : JSON.stringify(payload.token);

      const paymentMethod: PaymentMethod = {
        paymentMethodType: payload.paymentMethodType,
        walletType: payload.walletType,
        jsonPayload: jsonPayload,
        ack: ack.value,
        paymentMethodName: nickName.value,
      };

      setWalletPaymentMethod(paymentMethod);
    }
  };

  const handleSaveClick = async () => {
    if (
      !walletPaymentMethod ||
      !ack.value ||
      !nickName.value.trim() ||
      !(
        action === PaymentActionType.SAVE ||
        action === PaymentActionType.SAVE_AND_PAY_LATER
      )
    ) {
      return;
    }

    const paymentMethod: PaymentMethod = {
      ...walletPaymentMethod,
      ack: ack.value,
      paymentMethodName: nickName.value,
    };

    try {
      await requestDispatch<SavePaymentMethod, PaymentMethod>(
        savePaymentMethod,
        paymentMethod,
      )
        .unwrap()
        .then((res: PaymentMethod) => {
          if (buttonClick?.count) {
            if (paymentHandler) paymentHandler(res);
          } else {
            dispatch(updateSlide({ id: "Confirmation" }));
          }
        });
    } catch (err) {
      const errCode = extractError(err);
      addNotification({
        messages: errCode,
        type: "ERROR",
        closable: true,
      });
    }
  };

  return (
    <>
      <div className={classes.Container}>
        {providers.map((provider) => (
          <div key={provider.walletType} className={classes.ProviderWrapper}>
            {provider.walletType === EWALLET_TYPE.ApplePay && (
              <ApplePay
                amount={paymentAmount}
                onTokenReceived={(payload) =>
                  handleWalletTokenReceived(payload, provider.walletType)
                }
              />
            )}
            {provider.walletType === EWALLET_TYPE.GooglePay && (
              <GooglePay
                amount={paymentAmount}
                onTokenReceived={(payload) =>
                  handleWalletTokenReceived(payload, provider.walletType)
                }
              />
            )}
          </div>
        ))}
      </div>
      <div>
        {action === PaymentActionType.PAY ? null : (
          <>
            <ft-formfield
              class={classes.Tnc}
              label={
                action === PaymentActionType.SAVE
                  ? t("payment_next:card_review")
                  : t("payment_next:save_method")
              }
            >
              <ft-checkbox
                id="ack"
                name="ack"
                ref={ack.ref}
                onClick={
                  buttonClick?.scheduleFlag || disabled
                    ? null
                    : ack.valueCheckedHandler
                }
                {...renderConditionalProp(ack.value, "checked")}
                {...renderConditionalProp(
                  buttonClick?.scheduleFlag || disabled ? true : false,
                  "disabled",
                )}
                required={true}
                validationMessage={"error"}
                data-testid="add-payment-method-type-review-and-tnc"
              ></ft-checkbox>
            </ft-formfield>
            <p className={classes.Confirm}>
              {action === PaymentActionType.SAVE && ack.value !== true
                ? t("payment_next:ach_save_confirm")
                : ""}
            </p>
          </>
        )}
        {(action === PaymentActionType.SAVE ||
          (action === PaymentActionType.SAVE_AND_PAY_LATER && ack.value)) && (
          <ft-textfield
            id="walletNickname"
            name="walletNickname"
            ref={nickName.ref}
            label={t("payment_next:wallet_nickname")}
            placeholder={t("payment_next:wallet_nickname")}
            maxLength={50}
            required
            outlined
            validationMessage={t("common:field_not_empty")}
            onInput={nickName.valueChangeHandler}
            value={nickName.value}
          ></ft-textfield>
        )}

        {walletPaymentMethod ? (
          <div className={classes.WalletInfo}>
            {t("common:ewallet_success-text")}
          </div>
        ) : null}

        <p
          className={classes.Confirm}
          style={{ display: disabled ? "none" : "block" }}
        >
          {action === PaymentActionType.SAVE && ack.value !== true
            ? t("payment_next:ach_save_confirm")
            : ""}
        </p>
        {action === PaymentActionType.SAVE ? (
          <div className={classes.FormButtons}>
            <ft-button
              class={[classes.Back, "outline-button"].join(" ")}
              label={t("common:back_label")}
              outlined
              onClick={() => {
                dispatch(updateSlide({ id: "ManageWallet" }));
              }}
              data-testid="add-payment-method-back"
            ></ft-button>
            <ft-button
              class={classes.Create}
              label={t("common:save_label")}
              unelevated
              onClick={handleSaveClick}
              {...renderConditionalProp(ack.value !== true, "disabled")}
            ></ft-button>
          </div>
        ) : null}
      </div>
    </>
  );
};
